/**
 * features/finance/DetailedLedger.tsx
 * ------------------------------------------------------------------
 * THE SUBSIDIARY LEDGER — one party's account, transaction by transaction.
 *
 * The General Ledger answers what the school owes and is owed in total.
 * It cannot answer what THIS pupil has been charged and paid, or what
 * this supplier has billed and been paid. Those live in the sub-ledgers,
 * and there was nowhere in the app to open one.
 *
 * Statements are FAMILY-scoped by design, because siblings are billed
 * together. That is right for a parent and wrong the moment somebody
 * needs a single child's account — a transfer, a scholarship review, a
 * dispute about one pupil's fees.
 *
 * THE DISCOUNT IS SHOWN, NOT NETTED. An invoice is charged at its
 * subtotal and anything given away sits beneath it as its own credit.
 * "What did the school forgo, and to whom" is a question this screen has
 * to be able to answer; netting the two destroys it permanently.
 *
 * IT SAYS WHETHER IT TIES. The sub-ledger and the invoice records are two
 * routes to the same number, and a difference means one of them is wrong.
 * That reconciliation is the first thing an auditor does by hand, so the
 * screen does it on every render rather than waiting to be asked.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import {
  BookOpen, Users, Building2, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useData } from '@/contexts/DataContext';
import { useFinanceData } from '@/features/finance/useFinanceData';
import {
  buildPupilLedger, pupilLedgerTies, PUPIL_ENTRY_LABEL,
  type PupilEntryKind,
} from '@/lib/pupilLedger';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass, ExportBar,
} from '@/features/finance/ui';
import { downloadTablePdf, downloadCsv, type TableExport } from '@/lib/tableExport';
import { SCHOOL } from '@/lib/school.config';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const KIND_TONE: Record<PupilEntryKind, string> = {
  invoice: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  discount: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  receipt: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  credit_note: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  write_off: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
  refund: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
};

export default function DetailedLedger() {
  const { students } = useData();
  const fin = useFinanceData();
  const [studentId, setStudentId] = useState('');
  const [search, setSearch] = useState('');

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = [...students].sort((a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? ''));
    return q ? list.filter((s) => (s.fullName ?? '').toLowerCase().includes(q)) : list;
  }, [students, search]);

  const student = students.find((s) => s.id === studentId);

  const ledger = useMemo(
    () => (studentId
      ? buildPupilLedger(studentId, fin.invoices, fin.payments, fin.adjustments ?? [])
      : null),
    [studentId, fin.invoices, fin.payments, fin.adjustments],
  );

  const tie = useMemo(
    () => (ledger && studentId ? pupilLedgerTies(ledger, fin.invoices, studentId) : null),
    [ledger, fin.invoices, studentId],
  );

  if (fin.loading) return <div className="p-6 flex justify-center"><Spinner /></div>;

  function exportSpec(): TableExport<NonNullable<typeof ledger>['entries'][number]> {
    return {
      title: 'PUPIL ACCOUNT',
      meta: `${student?.fullName ?? ''} · as at ${new Date().toISOString().slice(0, 10)}`,
      columns: [
        { header: 'Date', text: (e) => e.date },
        { header: 'Type', text: (e) => PUPIL_ENTRY_LABEL[e.kind] },
        { header: 'Reference', text: (e) => e.reference },
        { header: 'Description', text: (e) => e.description },
        { header: 'Charge', text: (e) => (e.charge ? money(e.charge) : ''), raw: (e) => e.charge || null, align: 'right' },
        { header: 'Credit', text: (e) => (e.credit ? money(e.credit) : ''), raw: (e) => e.credit || null, align: 'right' },
        { header: 'Balance', text: (e) => money(e.balance), raw: (e) => e.balance, align: 'right' },
      ],
      rows: ledger?.entries ?? [],
      totals: ['', '', '', 'Outstanding', '', '', money(ledger?.outstanding ?? 0)],
      note: `Invoices are charged at their full amount and any discount shown as its own `
        + `credit, so what was given away stays visible. ${SCHOOL.name}.`,
      filename: `pupil-account-${(student?.fullName ?? 'pupil').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`,
    };
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        icon={BookOpen} tone="navy"
        title="Detailed Ledger"
        subtitle="One pupil's account, transaction by transaction — not the family statement"
        actions={ledger && ledger.entries.length > 0
          ? <ExportBar onPdf={() => downloadTablePdf(exportSpec())}
              onCsv={() => downloadCsv(exportSpec())} />
          : undefined}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        <SoftCard pad="p-0" className="h-fit">
          <PanelHeader icon={Users} title="Pupil" tone="navy"
            subtitle={`${matches.length} of ${students.length}`} />
          <div className="p-3">
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Find a pupil…" className={glassFieldClass()} />
          </div>
          <div className="max-h-[520px] overflow-y-auto pb-2">
            {matches.slice(0, 300).map((s) => (
              <button key={s.id} type="button" onClick={() => setStudentId(s.id)}
                className={`w-full text-left px-4 py-2 text-sm border-l-4 transition-colors ${
                  studentId === s.id
                    ? 'border-primary bg-primary/5 font-medium text-gray-900 dark:text-white'
                    : 'border-transparent text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800/50'
                }`}>
                <span className="truncate block">{s.fullName}</span>
              </button>
            ))}
            {matches.length > 300 && (
              <p className="px-4 py-2 text-[11px] text-gray-400">
                {matches.length - 300} more — narrow the search.
              </p>
            )}
          </div>
        </SoftCard>

        <div>
          {!student || !ledger ? (
            <SoftCard>
              <p className="text-sm text-gray-500 text-center py-8">
                Choose a pupil to read their account.
              </p>
            </SoftCard>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <StatChip tone="slate" value={money(ledger.charged)} label="Charged" />
                <StatChip tone="sky" value={money(ledger.discounted)} label="Given away" />
                <StatChip tone="emerald" value={money(ledger.received)} label="Received" />
                <StatChip tone={ledger.outstanding > 0 ? 'navy' : 'emerald'}
                  value={money(ledger.outstanding)} label="Outstanding" />
              </div>

              {/* The reconciliation an auditor does by hand, done on render. */}
              {tie && !tie.ties && (
                <SoftCard className="mb-4 border-l-4 border-red-400">
                  <p className="text-sm text-red-700 dark:text-red-300">
                    <AlertTriangle size={15} className="inline mr-1 -mt-0.5" />
                    This account totals <strong>{money(tie.ledger)}</strong> but the invoice
                    records say <strong>{money(tie.invoices)}</strong> — a difference of{' '}
                    <strong>{money(Math.abs(tie.difference))}</strong>. One of the two is
                    wrong, and a payment recorded against a voided invoice is the usual cause.
                  </p>
                </SoftCard>
              )}
              {tie && tie.ties && ledger.entries.length > 0 && (
                <p className="text-[12px] text-gray-400 mb-3 flex items-center gap-1.5">
                  <CheckCircle2 size={13} className="text-emerald-600" />
                  Agrees with the invoice records.
                </p>
              )}

              <SoftCard pad="p-0">
                <PanelHeader icon={BookOpen} tone="navy" title={student.fullName}
                  subtitle="Oldest first, with the balance after each entry" />

                {ledger.entries.length === 0 ? (
                  <p className="p-6 text-sm text-gray-500 text-center">
                    Nothing has been charged to this pupil.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                          <th className="py-2 pl-4 pr-2 font-semibold">Date</th>
                          <th className="px-2 font-semibold">Type</th>
                          <th className="px-2 font-semibold">Reference</th>
                          <th className="px-2 font-semibold">Description</th>
                          <th className="px-2 font-semibold text-right">Charge</th>
                          <th className="px-2 font-semibold text-right">Credit</th>
                          <th className="px-2 pr-4 font-semibold text-right">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledger.entries.map((e) => (
                          <tr key={e.id} className="border-b border-gray-50 dark:border-gray-800">
                            <td className="py-2 pl-4 pr-2 tabular-nums text-gray-600 dark:text-gray-300">
                              {e.date}
                            </td>
                            <td className="px-2">
                              <span className={`text-[11px] px-1.5 py-0.5 rounded font-medium ${KIND_TONE[e.kind]}`}>
                                {PUPIL_ENTRY_LABEL[e.kind]}
                              </span>
                            </td>
                            <td className="px-2 font-mono text-xs text-gray-500">{e.reference}</td>
                            <td className="px-2 text-gray-700 dark:text-gray-200 truncate max-w-[240px]">
                              {e.description}
                            </td>
                            <td className="px-2 text-right tabular-nums">
                              {e.charge ? money(e.charge) : ''}
                            </td>
                            <td className="px-2 text-right tabular-nums text-emerald-700 dark:text-emerald-400">
                              {e.credit ? money(e.credit) : ''}
                            </td>
                            <td className="px-2 pr-4 text-right tabular-nums font-medium">
                              {money(e.balance)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-gray-200 dark:border-gray-700 font-bold">
                          <td className="py-2.5 pl-4 pr-2" colSpan={6}>Outstanding</td>
                          <td className="px-2 pr-4 text-right tabular-nums">
                            {money(ledger.outstanding)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </SoftCard>

              {/* The other sub-ledger lives on its own screen, and saying so
                  is cheaper than somebody concluding it does not exist. */}
              <p className="text-[12px] text-gray-400 mt-3 flex items-center gap-1.5">
                <Building2 size={13} />
                A supplier&rsquo;s account reads the same way under Supplier Statements.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
